import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Label } from '@twilio-paste/label';
import { TextArea } from '@twilio-paste/textarea';
import { HelpText } from '@twilio-paste/help-text';
import { Anchor } from '@twilio-paste/anchor';
import { Box } from '@twilio-paste/box';
import { Button } from '@twilio-paste/button';
import { Table, THead, TBody, Td, Th, Tr } from '@twilio-paste/table';
import { InformationIcon } from '@twilio-paste/icons/esm/InformationIcon';
import { UnorderedList, ListItem } from '@twilio-paste/list';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { Codeblock } from '../../../components/codeblock';
import { SidebarCategoryRoutes } from '../../../constants';
import Changelog from '@twilio-paste/textarea/CHANGELOG.md';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/textarea"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/textarea/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name="Textarea" categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/textarea" storybookUrl="/?path=/story/components-textarea--textarea-options" abstractUrl="https://share.goabstract.com/6cc5fc40-3408-4cfd-b1d0-103aea01601d" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <p>{`A textarea allows users to enter text on multiple lines.`}</p>
        <h3>{`About Textarea`}</h3>
        <p>{`Use a textarea when you want to allow a user to enter text on multiple lines. A textarea allows for any text to be entered and can't be restricted as other `}<a parentName="p" {...{
            "href": "/form/input/"
          }}>{`inputs`}</a>{` can.`}</p>
        <h3>{`Accessibility`}</h3>
        <UnorderedList mdxType="UnorderedList">
  <ListItem mdxType="ListItem">
    Include a visible label on <strong>all</strong> form fields.
  </ListItem>
  <ListItem mdxType="ListItem">
    Each label must use the <inlineCode>htmlFor</inlineCode> prop that equals the <inlineCode>id</inlineCode> of the
    associated textarea.
  </ListItem>
  <ListItem mdxType="ListItem">
    Don't use placeholder text as a replacement for labels. It can be used to provide an example to users, but will
    disappear from the field when a user enters text. It's also not broadly supported by assistive technologies and
    won't display in older browsers.
  </ListItem>
  <ListItem mdxType="ListItem">
    Use one of 3 ways to label a form field:
    <UnorderedList marginBottom="space0" mdxType="UnorderedList">
      <ListItem mdxType="ListItem">
        Visible label with <Anchor href="#formlabel-props" mdxType="Anchor">Label</Anchor> (preferred)
      </ListItem>
      <ListItem mdxType="ListItem">
        Visible label that's associated to the textarea with <inlineCode>aria-labelledby</inlineCode>
      </ListItem>
      <ListItem mdxType="ListItem">
        Label directly using <inlineCode>aria-label</inlineCode>
      </ListItem>
    </UnorderedList>
  </ListItem>
  <ListItem mdxType="ListItem">
    Provide clear identification of required fields in the label or at the start of a form. If you use the required
    field indicator, include the form key at the start of the form.
    <UnorderedList marginBottom="space0" mdxType="UnorderedList">
      <ListItem mdxType="ListItem">
        Use the <inlineCode>required</inlineCode> prop to programatically indicate they are required to browsers.
      </ListItem>
    </UnorderedList>
  </ListItem>
  <ListItem mdxType="ListItem">
    Include inline error text with the error icon on any field that errors to make it visually clear that the field
    changed.
  </ListItem>
  <ListItem mdxType="ListItem">
    If the textarea has associated help text or error text, include the <inlineCode>aria-describedby</inlineCode> prop
    on the textarea. This should match the <inlineCode>id</inlineCode> of the help or error text.
  </ListItem>
        </UnorderedList>
        <h2>{`Examples`}</h2>
        <h3>{`Textarea`}</h3>
        <p>{`The textarea should include the base textarea, along with supporting elements to compose a textarea field that gives the user the context they need to successfully fill it out:`}</p>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Label`}</strong>{` — A label should be included for every form field and provide a short title for its associated textarea.`}</li>
          <li parentName="ul"><strong parentName="li">{`Required field indicator`}</strong>{` — In a form where there are fewer or as many required fields as optional, use a required indicator to show users which fields are required to be filled out.`}</li>
          <li parentName="ul"><strong parentName="li">{`Help text`}</strong>{` — Text that's placed below the field to help users prevent an error and describe what makes the form field successful`}</li>
        </ul>
        <Callout mdxType="Callout">
  <CalloutTitle as="h4" mdxType="CalloutTitle">Hot accessibility tip</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    Make sure to connect the <inlineCode>Label</inlineCode> to the <inlineCode>TextArea</inlineCode>. This is done with
    the <inlineCode>htmlFor</inlineCode> prop on the label, and the <inlineCode>id</inlineCode> prop on the textarea.
    Those two need to match.
  </CalloutText>
  <CalloutText mdxType="CalloutText">
    If the textarea has any associated help text, the textarea should also use the{' '}
    <inlineCode>aria-describedby</inlineCode> prop that equals the <inlineCode>id</inlineCode> of the help text. This
    ensures screen readers know the help text ties directly to the textarea.
  </CalloutText>
        </Callout>
        <LivePreview scope={{
          Label,
          HelpText,
          TextArea
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="message" required>Message (at least 120 characters)</Label>
  <TextArea onChange={()=>{}} onChange={()=>{}} aria-describedby="message_help_text" id="message" name="message" placeholder="Enter message" required />
  <HelpText id="message_help_text">Please enter at least 120 characters</HelpText>
</>`}
        </LivePreview>
        <h3>{`Textarea with add-ons (prefix/suffix text or icons)`}</h3>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Prefix/suffix text`}</strong>{` — Text that can be used as a prefix and/or suffix to the value that is entered. Use prefix/suffix to help users format text.`}</li>
          <li parentName="ul"><strong parentName="li">{`Icon`}</strong>{` — Icons can be placed in the same area as the prefix and suffix text. Icons should trigger an action (`}<em parentName="li">{`e.g.`}</em>{`, clearing a field).`}</li>
        </ul>
        <LivePreview scope={{
          Label,
          HelpText,
          TextArea
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="short_bio" required>Short Biography</Label>
  <TextArea onChange={()=>{}} id="short_bio" name="short_bio" insertBefore={<div>Bio</div>} required />
</>`}
        </LivePreview>
        <LivePreview scope={{
          Label,
          HelpText,
          TextArea,
          InformationIcon,
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="body" required>Body Copy</Label>
  <TextArea
    onChange={()=>{}}
    aria-describedby="body_help_text"
    id="body"
    name="body"
    placeholder="Ahoy, World"
    insertAfter={
      <Button variant="link">
        <InformationIcon decorative={false} size="sizeIcon20" title="Get more info" />
      </Button>
    }
  />
  <HelpText id="body_help_text">Please enter text for the copy area</HelpText>
</>`}
        </LivePreview>
        <h2>{`States`}</h2>
        <h3>{`Textarea with inline error`}</h3>
        <p>{`Change a form field to its error state and add an inline error if the value entered doesn't pass validation requirements.`}</p>
        <p>{`An inline error is placed below the field to inform a user of any errors in their value. If help text exists, the error text should replace and repeat the help text.`}</p>
        <LivePreview scope={{
          Label,
          HelpText,
          TextArea
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="text_error">Description</Label>
  <TextArea onChange={()=>{}} aria-describedby="text_error_help_text" id="text_error" name="text_error" hasError />
  <HelpText id="text_error_help_text" variant="error">Please enter a description</HelpText>
</>`}
        </LivePreview>
        <h3>{`Disabled textarea`}</h3>
        <p>{`Use a disabled form field to show users that they can't interact with the field.`}</p>
        <LivePreview scope={{
          Label,
          TextArea
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="text_disabled" disabled>Description (disabled)</Label>
  <TextArea onChange={()=>{}} id="text_disabled" name="text_disabled" placeholder="Enter a description..." disabled />
</>`}
        </LivePreview>
        <h3>{`Read-only textarea`}</h3>
        <p>{`Use a read-only form field when a field's value can't be changed, but users should be able to read or apply focus on the field. For example, use a read-only form field if a user needs to copy the value.`}</p>
        <LivePreview scope={{
          Label,
          TextArea
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="text_readonly">Description (read-only)</Label>
  <TextArea onChange={()=>{}} id="text_readonly" name="text_readonly" readOnly value="Paste is a design system used to build accessible, consistent, and high quality customer experiences at Twilio. Paste is open source and contributions are welcome." />
</>`}
        </LivePreview>
        <h2>{`Composition notes`}</h2>
        <p>{`A textarea field must have at least a label and a textarea.`}</p>
        <LivePreview scope={{
          Label,
          TextArea
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Label htmlFor="details">Details</Label>
  <TextArea onChange={()=>{}} id="details" name="details" />
</>`}
        </LivePreview>
        <h3>{`Positioning form fields`}</h3>
        <p>{`Stack form fields vertically with `}<inlineCode parentName="p">{`$space-80`}</inlineCode>{` spacing between each field.`}</p>
        <LivePreview scope={{
          Label,
          HelpText,
          TextArea,
          Box
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Box marginBottom="space80">
    <Label htmlFor="header">Header</Label>
    <TextArea onChange={()=>{}} id="header" name="header" placeholder="Enter header content..." />
  </Box>
  <Box>
    <Label htmlFor="footer">Footer</Label>
    <TextArea onChange={()=>{}} id="footer" name="footer" placeholder="Enter footer content..." />
  </Box>
</>`}
        </LivePreview>
        <p>{`Avoid placing multiple form fields on the same horizontal row to help make it easier to scan a page vertically. Use the Grid component if you need to place form fields horizontally.`}</p>
        <h3>{`Labels and help text`}</h3>
        <p>{`Labels should clearly describe the value being requested. They should be concise and descriptive, not instructional. To do this:`}</p>
        <ul>
          <li parentName="ul">{`Use help text to provide instruction if needed. For example, don't use "Enter the registration code on the back of your SIM card below" as label text. Instead, use "SIM registration code" as a label and "Find the registration code on the back of your SIM card" as help text.`}</li>
          <li parentName="ul">{`Avoid articles ("a", "the") and punctuation. For example, use "SIM registration code" rather than "The SIM registration code".`}</li>
        </ul>
        <p>{`To support internationalization, avoid starting a sentence with the label and using the field to finish it since sentence structures vary across languages. For example, use "Call log expiration date" or "How long should logs be stored?". Don't use "Remove logs after:".`}</p>
        <p>{`Give users enough information in help text to prevent textarea and formatting errors. Keep it concise and scoped to information that will help with validation. For example, use help text if a password field has specific requirements that a user should know prior to filling it out.`}</p>
        <h3>{`Required field indicator`}</h3>
        <p>{`Ask for information only when needed. Consider removing the field otherwise.`}</p>
        <p>{`Use required indicators to show users which fields they must fill out.`}</p>
        <h3>{`Validation`}</h3>
        <p>{`Validate form fields on form submission.`}</p>
        <p>{`Validating a form field when the user leaves the current field (on blur) can be helpful to check for syntax errors. However, this can be frustrating to users who tab through controls to navigate a page, and to screen reader users, who might not be able to detect that an error occurred on blur.`}</p>
        <p>{`Don't prevent form submission by disabling the submit button. An error message can give more information than a disabled button can to help users figure out which fields are invalid.`}</p>
        <h3>{`Errors`}</h3>
        <p>{`Use inline error text to explain how to fix an error.`}</p>
        <p>{`Ideally, help text should have enough information to help users prevent errors. If help text exists and you need to show an error, the error text should replace and repeat the help text until the error has been resolved.`}</p>
        <p>{`Error text should:`}</p>
        <ul>
          <li parentName="ul">{`Be actionable. Explain how to fix an error and if reasonable, why it happened so that it might also be prevented in the future.`}</li>
          <li parentName="ul">{`Be concise and simple, maybe even more than normal. Avoid jargon. Try to keep error text to 2 sentences or fewer.`}</li>
          <li parentName="ul">{`Use the passive voice for textarea errors to avoid placing blame on the user. For example, "A friendly name is required."`}</li>
          <li parentName="ul">{`Use the active voice for system errors. For example, "Our systems are currently down. Please contact our support team."`}</li>
        </ul>
        <h3>{`Optional compositional elements`}</h3>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Prefix/suffix`}</strong>{` — Use a prefix or suffix to help users format their textarea and to provide more context about the value a user is entering. For example, you could prefix or suffix a field with a currency symbol, or use suffix to append a character counter. Make sure to consider internationalization when using prefixes or suffixes since formatting may differ across languages. For example, currency symbols are placed on the left in American English, while they're placed on the right in French. Don't use prefix/suffix text as a replacement for a label.`}</li>
          <li parentName="ul"><strong parentName="li">{`Icon`}</strong>{` — Use an icon if you need to give the user additional controls for the field. For example, use an icon to clear a field on click, removing the need for users to manually delete their entered value. Place icon buttons that trigger an action on the right side of the field. If you need 2 actions on a field (e.g., popover trigger and clear field), place the icon button that affects the field value on the right, and the other icon on the left.`}</li>
          <li parentName="ul"><strong parentName="li">{`Placeholder`}</strong>{` — Use a placeholder when you want to give an example of the type of data a user should enter, mainly to help with recall. For example, when asking for links to social media profiles, you could give examples of a few social media sites. Placeholders should be used in addition to the label and not as a replacement since they'll disappear when a user begins entering their own value. If you need to show supporting instructions, use help text instead.`}</li>
        </ul>
        <h2>{`When to use an textarea`}</h2>
        <p>{`Use a textarea when users are expected to enter text that exceeds a single line, usually longer than a sentence.`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use a textarea to encourage longer text entry." center mdxType="Do">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="long_do" mdxType="Label">Tell us your life story</Label>
      <TextArea onChange={() => {}} id="long_do" name="long_do" mdxType="TextArea" />
    </Box>
  </Do>
  <Dont title="Don't" body="Don't use a textarea when text entry is expected to be short since it could confuse users. Use an input instead." center mdxType="Dont">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="long_dont" mdxType="Label">First Name</Label>
      <TextArea onChange={() => {}} id="long_dont" name="long_dont" mdxType="TextArea" />
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="If you limit the length of text entry, show a character counter and explain to users in help text why their entry is restricted." center mdxType="Do">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="limited_do" mdxType="Label">Job Description (120 characters)</Label>
      <TextArea onChange={() => {}} aria-describedby="limited_do_help_text" id="limited_do" name="limited_do" mdxType="TextArea" />
      <HelpText id="limited_do_help_text" mdxType="HelpText">Limit to 120 characters</HelpText>
    </Box>
  </Do>
  <Dont title="Don't" body="Don't have a character limit if you can't explain to the user why their text entry is restricted." center mdxType="Dont">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="limited_dont" mdxType="Label">State Moto</Label>
      <TextArea onChange={() => {}} id="limited_dont" name="limited_dont" mdxType="TextArea" />
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use help text to help users prevent errors and fill out a form field correctly." center mdxType="Do">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="help_do" mdxType="Label">Enter the correct phrase</Label>
      <TextArea onChange={() => {}} aria-describedby="help_do_help_text" id="help_do" name="help_do" mdxType="TextArea" />
      <HelpText id="help_do_help_text" mdxType="HelpText">Please enter: Paste is open source and contributions are welcome.</HelpText>
    </Box>
  </Do>
  <Dont title="Don't" body="Don't use placeholder text for validation instructions." center mdxType="Dont">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="help_dont" mdxType="Label">Enter the correct phrase</Label>
      <TextArea onChange={() => {}} id="help_dont" name="help_dont" placeholder="Please enter: Paste is open source and contributions are welcome." mdxType="TextArea" />
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Keep help text and error text concise and simple. If you need to use more than 2 sentences to explain a field, link out to supporting docs or trigger a popover instead." center mdxType="Do">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="error_do" required mdxType="Label">
        Header
      </Label>
      <TextArea onChange={() => {}} id="error_do" name="error_do" required hasError mdxType="TextArea" />
      <HelpText variant="error" mdxType="HelpText">This is a required field. Please enter some text.</HelpText>
    </Box>
  </Do>
  <Dont title="Don't" body="Don't use more than 2 sentences in help text or error text." center mdxType="Dont">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="error_dont" required mdxType="Label">
        Phone Number
      </Label>
      <TextArea onChange={() => {}} id="error_dont" name="error_dont" required hasError mdxType="TextArea" />
      <HelpText variant="error" mdxType="HelpText">
        This field is needed to submit your document. If you don't enter any text, your submission will fail. Please
        enter some text into the field. This will make the field valid.
      </HelpText>
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Include a visible label on every form field." center mdxType="Do">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="label_do" mdxType="Label">Footer Text</Label>
      <TextArea onChange={() => {}} id="label_do" name="label_do" mdxType="TextArea" />
    </Box>
  </Do>
  <Dont title="Don't" body="Don't use prefix/suffix text as a replacement for a label." center mdxType="Dont">
    <Box width="100%" padding="space60" mdxType="Box">
      <TextArea onChange={() => {}} id="label_dont" name="label_dont" insertBefore={<div>Footer Text</div>} mdxType="TextArea" />
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use a disabled form field to show users that they can't interact with the field, but that it could potentially be enabled through another UI element." center mdxType="Do">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="disabled_do" mdxType="Label">Legal Use Only</Label>
      <TextArea onChange={() => {}} id="disabled_do" name="disabled_do" disabled mdxType="TextArea" />
    </Box>
  </Do>
  <Dont title="Don't" body="Don't use a disabled form field to show information that can't be edited. Instead, use a read-only form field or consider another way of showing static information." center mdxType="Dont">
    <Box width="100%" padding="space60" mdxType="Box">
      <Label htmlFor="disabled_dont" mdxType="Label">Company Information</Label>
      <TextArea onChange={() => {}} id="disabled_dont" name="disabled_dont" disabled value="Twilio powers the future of business communications." mdxType="TextArea" />
    </Box>
  </Dont>
        </DoDont>
        <h2>{`Anatomy`}</h2>
        <Box marginBottom="space60" mdxType="Box">
  <Table tableLayout="fixed" mdxType="Table">
    <THead mdxType="THead">
      <Tr mdxType="Tr">
        <Th mdxType="Th">Property</Th>
        <Th mdxType="Th">Default token</Th>
        <Th mdxType="Th">Modifiable?</Th>
      </Tr>
    </THead>
    <TBody mdxType="TBody">
      <Tr mdxType="Tr">
        <Td mdxType="Td">Label text</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">$font-size-30</ListItem>
            <ListItem mdxType="ListItem">Default: $color-text, $font-weight-semibold</ListItem>
            <ListItem mdxType="ListItem">Disabled: $color-text-weaker, $font-weight-semibold</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Required field indicator</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">4px size (0.25rem)</ListItem>
            <ListItem mdxType="ListItem">Background: $color-background-required</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Box Shadow</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Default: $shadow-border</ListItem>
            <ListItem mdxType="ListItem">Hover: $shadow-border-primary-dark</ListItem>
            <ListItem mdxType="ListItem">Disabled: $shadow-border-light</ListItem>
            <ListItem mdxType="ListItem">Error: $shadow-border-error</ListItem>
            <ListItem mdxType="ListItem">Error hover: $shadow-border-error-dark</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Background</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Default: $color-background-body</ListItem>
            <ListItem mdxType="ListItem">Disabled, Readonly: $color-background</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Prefix/suffix</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Background: $color-background</ListItem>
            <ListItem mdxType="ListItem">Border: $color-border-lighter</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Prefix/suffix text</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Default: $color-text</ListItem>
            <ListItem mdxType="ListItem">Disabled: $color-text-weak</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Value text</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">$font-size-30</ListItem>
            <ListItem mdxType="ListItem">Default, Readonly: $color-text</ListItem>
            <ListItem mdxType="ListItem">Disabled: $color-text-weaker</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No, but it can take any children</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Placeholder text</Td>
        <Td mdxType="Td">$color-text-weak, italic</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Help text</Td>
        <Td mdxType="Td">$color-text-weak, $font-size-30</Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Inline error</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Text: $color-text-error, $font-size-30</ListItem>
            <ListItem mdxType="ListItem">Icon: IconError, $color-text-error, $icon-size-20</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Spacing</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">
              Label:
              <UnorderedList marginBottom="space0" mdxType="UnorderedList">
                <ListItem mdxType="ListItem">Bottom: $space-10</ListItem>
                <ListItem mdxType="ListItem">Between label and required indicator: $space-20</ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem mdxType="ListItem">
              Textarea:
              <UnorderedList marginBottom="space0" mdxType="UnorderedList">
                <ListItem mdxType="ListItem">Left padding: $space-40</ListItem>
                <ListItem mdxType="ListItem">Top, right, bottom padding: $space-30</ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem mdxType="ListItem">Prefix/suffix padding: $space-30</ListItem>
            <ListItem mdxType="ListItem">
              Help text:
              <UnorderedList marginBottom="space0" mdxType="UnorderedList">
                <ListItem mdxType="ListItem">Top: $space-20</ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem mdxType="ListItem">
              Inline error:
              <UnorderedList marginBottom="space0" mdxType="UnorderedList">
                <ListItem mdxType="ListItem">Spacing between icon and text: $space-20</ListItem>
                <ListItem mdxType="ListItem">Top: $space-20</ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
    </TBody>
  </Table>
        </Box>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/textarea - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {TextArea, Label, HelpText} from '@twilio-paste/core';

const Component = () => (
  <>
    <Label htmlFor="foo" required>
      foo
    </Label>
    <TextArea onChange={() => {}} aria-describedby="foo_text" id="foo" name="foo" placeholder="foo" required />
    <HelpText id="foo_text">Please enter some text</HelpText>
  </>
);
`}</code></pre>
        <h4>{`TextArea Props`}</h4>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`textarea`}</inlineCode>{` are supported including the following props:`}</p>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`id?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the id of the textarea. Should match the htmlFor of `}<inlineCode parentName="td">{`Label`}</inlineCode>{`.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`name?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the name of the textarea.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`placeholder?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the placeholder of the textarea.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`disabled?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`boolean`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Disables the textarea.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`readOnly?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`boolean`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the textarea to readonly.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`required?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`boolean`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the textarea as required.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`hasError?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`boolean`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the textarea to an error state.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onChange?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onFocus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onBlur?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default', 'inverse'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default'`}</td>
            </tr>
          </tbody>
        </table>
        <h4>{`Label Props`}</h4>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`label`}</inlineCode>{` are supported including the following props:`}</p>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`htmlFor`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the for of the label. Should match the id of `}<inlineCode parentName="td">{`TextArea`}</inlineCode>{`. Required.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`disabled?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`boolean`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Shows the textarea is disabled.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`required?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`boolean`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Shows the textarea is required.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginBottom?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'space0', 'space10'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'space10'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant ?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default', 'inverse'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default'`}</td>
            </tr>
          </tbody>
        </table>
        <h4>{`HelpText Props`}</h4>
        <p>{`All the valid HTML attributes (role, aria-`}{`*`}{`, type, and so on) including the following props:`}</p>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginTop?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'space0', 'space20'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'space20'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default', 'error', 'error_inverse', 'inverse'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Changes the state.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default'`}</td>
            </tr>
          </tbody>
        </table>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      